"use client";

import { forwardRef, Fragment, useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { apiFetch } from "@/src/lib/api-fetch";
import { AlertTriangle, Check, ChevronDown, ChevronRight, Copy, Info, Loader2, MessageSquareText } from "lucide-react";
import type { CallJob, RecentCall } from "@/lib/llm-models/costs";
import {
  costFilterParams,
  costRangeParams,
  PROMPT_RETENTION_DAYS,
  STATUS_FILTER_LABELS,
  STATUS_FILTERS,
  STATUS_LABELS,
  type CallStatus,
  type CostFilters,
  type CostRange,
  type StatusFilter,
} from "@/lib/llm-models/cost-filters";
import { FilterListbox, focusRing, SearchBox } from "./CostFilterControls";
import { costUsd, formatPakistanTime } from "./format";

type JobsResponse = { jobs: CallJob[]; next: string | null; total: number };
type JobCallsResponse = { calls: RecentCall[]; outside: number; truncated: boolean };
type MessagesResponse =
  | {
      available: true;
      prompt: { role: string; content: string }[];
      reply: string | null;
    }
  | { available: false; message: string };

async function getJson<T>(url: string, what: string): Promise<T> {
  const res = await apiFetch(url);
  if (res.status === 401) throw new Error("Your session has expired. Sign in again.");
  if (res.status === 403) throw new Error("Only inaraX admins can see AI calls.");
  if (!res.ok) throw new Error(`Failed to load ${what}.`);
  return (await res.json()) as T;
}

const STATUS_TONES: Record<string, string> = {
  ok: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  error: "bg-red-50 text-red-700 ring-red-200",
  empty: "bg-amber-50 text-amber-800 ring-amber-200",
  truncated: "bg-amber-50 text-amber-800 ring-amber-200",
};

export function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${
        STATUS_TONES[status] ?? "bg-slate-50 text-slate-700 ring-slate-200"
      }`}
    >
      {STATUS_LABELS[status as CallStatus] ?? status}
    </span>
  );
}

const PHONE_QUERY = "(max-width: 639px)";

/**
 * Whether the screen is phone-sized (below Tailwind's `sm`). Only the layout that fits is
 * rendered, so an opened job is mounted (and fetched) once. Server render assumes a wide screen.
 */
function subscribeToScreenSize(onChange: () => void): () => void {
  const query = window.matchMedia(PHONE_QUERY);
  query.addEventListener("change", onChange);
  return () => query.removeEventListener("change", onChange);
}
const isPhoneNow = () => window.matchMedia(PHONE_QUERY).matches;
const isPhoneOnServer = () => false;

function useIsPhone(): boolean {
  return useSyncExternalStore(subscribeToScreenSize, isPhoneNow, isPhoneOnServer);
}

function seconds(ms: number): string {
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)} s`;
}

/** "Lesson translation · Arabic" for a job in another language. */
function taskWithLanguage(j: CallJob): string {
  return j.language && j.language !== "English" ? `${j.task} · ${j.language}` : j.task;
}

/**
 * Who caused the job: the learner's email (or Clerk id when the email isn't known). Jobs with no
 * learner (translations, quizzes, placement quizzes) are started from the admin side, which
 * doesn't record who clicked, so they read "Admin".
 */
function personText(j: CallJob): string {
  return j.learnerEmail ?? j.learner ?? "Admin";
}

function lessonText(c: { lessonTitle: string | null; lessonId: number | null }): string | null {
  if (c.lessonTitle) return c.lessonTitle;
  return c.lessonId !== null ? `Lesson ${c.lessonId}` : null;
}

function CopyId({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span className="inline-flex max-w-full items-start gap-1.5">
      <span className="min-w-0 break-all font-mono text-[13px]">{value}</span>
      <button
        type="button"
        aria-label="Copy"
        onClick={() => {
          void navigator.clipboard?.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        className={`mt-0.5 shrink-0 rounded p-0.5 text-slate-400 hover:text-slate-700 ${focusRing}`}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-800">{children}</dd>
    </div>
  );
}

function roleLabel(role: string): string {
  if (role === "developer" || role === "system") return "Instructions";
  if (role === "user") return "Prompt";
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function Messages({ callId }: { callId: number }) {
  const [data, setData] = useState<MessagesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getJson<MessagesResponse>(`/api/admin/ai-models/costs/calls/${callId}/messages`, "the prompt and reply")
      .then((body) => {
        if (!cancelled) setData(body);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load the prompt and reply.");
      });
    return () => {
      cancelled = true;
    };
  }, [callId]);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!data) {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the prompt and reply…
      </p>
    );
  }
  if (!data.available) return <p className="text-sm text-slate-500">{data.message}</p>;

  return (
    <div className="space-y-3">
      {data.prompt.map((m, i) => (
        <div key={i}>
          <p className="text-xs font-medium text-slate-500">{roleLabel(m.role)}</p>
          <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white p-3 font-sans text-[13px] leading-relaxed text-slate-700 ring-1 ring-inset ring-slate-200">
            {m.content}
          </pre>
        </div>
      ))}
      <div>
        <p className="text-xs font-medium text-slate-500">Reply</p>
        {data.reply ? (
          <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-indigo-50/50 p-3 font-sans text-[13px] leading-relaxed text-slate-800 ring-1 ring-inset ring-indigo-100">
            {data.reply}
          </pre>
        ) : (
          <p className="mt-1 text-sm text-slate-500">The model sent no reply.</p>
        )}
      </div>
    </div>
  );
}

/** Why a call without an error message still went wrong. */
function statusNote(c: RecentCall): string | null {
  if (c.status === "empty") {
    return c.reasoningTokens && c.tokensOut && c.reasoningTokens >= c.tokensOut
      ? "The model sent back nothing. It used its whole output allowance on thinking."
      : "The model sent back nothing.";
  }
  if (c.status === "truncated") return "The reply hit the output limit set on the Models tab and was cut off.";
  return null;
}

/** One call's own details: what went wrong, its model and tokens, and its prompt and reply. */
function CallDetails({ call }: { call: RecentCall }) {
  const [showMessages, setShowMessages] = useState(false);
  const note = statusNote(call);
  return (
    <div className="space-y-3 px-4 pb-4 pt-1 sm:pl-12 sm:pr-5">
      {call.error ? (
        <div className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-sm text-red-800 ring-1 ring-inset ring-red-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
          <p className="min-w-0 break-words">{call.error}</p>
        </div>
      ) : note ? (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900 ring-1 ring-inset ring-amber-200">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
          <p>{note}</p>
        </div>
      ) : null}
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 min-[480px]:grid-cols-2 lg:grid-cols-4">
        <Detail label="Model">{call.modelName}</Detail>
        <Detail label="Tokens in / out">
          {call.tokensIn === null && call.tokensOut === null
            ? "Not reported"
            : `${(call.tokensIn ?? 0).toLocaleString()} / ${(call.tokensOut ?? 0).toLocaleString()}`}
        </Detail>
        {call.reasoningTokens ? <Detail label="Thinking tokens">{call.reasoningTokens.toLocaleString()}</Detail> : null}
        <Detail label="Time taken">{seconds(call.latencyMs)}</Detail>
        {call.generationId ? (
          <Detail label="OpenRouter id">
            <CopyId value={call.generationId} />
          </Detail>
        ) : null}
      </dl>
      {!call.generationId ? (
        <p className="text-sm text-slate-500">No prompt or reply was saved, because the call failed before the model answered.</p>
      ) : call.promptExpired ? (
        <p className="text-sm text-slate-500">Prompts and replies are kept for {PROMPT_RETENTION_DAYS} days, and this call is older.</p>
      ) : showMessages ? (
        <Messages callId={call.id} />
      ) : (
        <button
          type="button"
          onClick={() => setShowMessages(true)}
          className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 ${focusRing}`}
        >
          <MessageSquareText className="h-3.5 w-3.5" /> Show prompt and reply
        </button>
      )}
    </div>
  );
}

/**
 * An opened job: what it was for, then its calls in the order they were made, each named by
 * its step ("scenario section (attempt 1)") when inara recorded one. A one-call job opens that
 * call straight away.
 */
function JobDetails({ job, rangeQuery, onLearner }: { job: CallJob; rangeQuery: string; onLearner: (learner: string) => void }) {
  const [calls, setCalls] = useState<RecentCall[] | null>(null);
  const [extra, setExtra] = useState<{ outside: number; truncated: boolean }>({ outside: 0, truncated: false });
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<ReadonlySet<number>>(new Set());

  useEffect(() => {
    let cancelled = false;
    getJson<JobCallsResponse>(`/api/admin/ai-models/costs/jobs/${encodeURIComponent(job.key)}/calls?${rangeQuery}`, "the job's calls")
      .then((body) => {
        if (cancelled) return;
        setCalls(body.calls);
        setExtra({ outside: body.outside, truncated: body.truncated });
        if (body.calls.length === 1) setOpen(new Set([body.calls[0].id]));
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load the job's calls.");
      });
    return () => {
      cancelled = true;
    };
  }, [job.key, rangeQuery]);

  const toggle = (id: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const lesson = lessonText(job);
  return (
    <div className="space-y-4 bg-slate-50/70 px-4 py-4 sm:px-5">
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 min-[480px]:grid-cols-2 lg:grid-cols-4">
        {lesson ? <Detail label="Lesson">{job.lessonId !== null ? `${lesson} (${job.lessonId})` : lesson}</Detail> : null}
        {job.language ? <Detail label="Language">{job.language}</Detail> : null}
        {job.organization ? <Detail label="Organization">{job.organization}</Detail> : null}
        {!job.learner ? <Detail label="Triggered by">Admin</Detail> : null}
        {job.learner ? (
          <Detail label="Triggered by">
            {job.learnerEmail ?? <CopyId value={job.learner} />}
            <button
              type="button"
              onClick={() => onLearner(job.learner as string)}
              className={`mt-1 block rounded text-xs font-semibold text-indigo-600 hover:text-indigo-800 ${focusRing}`}
            >
              Show everything for this learner
            </button>
          </Detail>
        ) : null}
        <Detail label="Started">{formatPakistanTime(job.startedAt)}</Detail>
        {job.jobId ? (
          <Detail label="Background job id">
            <CopyId value={job.jobId} />
          </Detail>
        ) : null}
      </dl>

      <div>
        <p className="text-xs font-medium text-slate-500">
          {(calls?.length ?? job.calls) === 1 ? "The AI call" : `The ${calls?.length ?? job.calls} AI calls, in order`}
        </p>
        {error ? <p className="mt-2 text-sm text-red-700">{error}</p> : null}
        {!calls && !error ? (
          <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the calls…
          </p>
        ) : null}
        {calls ? (
          <ol className="mt-2 divide-y divide-slate-100 overflow-hidden rounded-lg bg-white ring-1 ring-slate-200">
            {calls.map((c, i) => {
              const isOpen = open.has(c.id);
              const Chevron = isOpen ? ChevronDown : ChevronRight;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    aria-expanded={isOpen}
                    aria-controls={`job-call-${c.id}`}
                    onClick={() => toggle(c.id)}
                    className={`flex w-full flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-left hover:bg-slate-50 ${focusRing}`}
                  >
                    <Chevron className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="w-5 shrink-0 text-xs tabular-nums text-slate-400">{i + 1}</span>
                    <span className="min-w-0 flex-1 text-sm font-medium text-slate-800">{c.step ?? `Call ${i + 1}`}</span>
                    <StatusBadge status={c.status} />
                    <span className="w-16 shrink-0 text-right text-xs tabular-nums text-slate-500">{seconds(c.latencyMs)}</span>
                    <span className="w-24 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-900">
                      {c.cost === null ? <span className="font-normal text-slate-400">-</span> : costUsd(c.cost)}
                    </span>
                  </button>
                  {isOpen ? (
                    <div id={`job-call-${c.id}`}>
                      <CallDetails call={c} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : null}
        {calls && (extra.outside > 0 || extra.truncated) ? (
          <p className="mt-2 text-xs text-slate-500">
            {[
              extra.truncated ? `Showing the first ${calls.length} calls.` : null,
              extra.outside > 0
                ? `${extra.outside} more ${extra.outside === 1 ? "call" : "calls"} in this job ${extra.outside === 1 ? "falls" : "fall"} outside this period.`
                : null,
            ]
              .filter(Boolean)
              .join(" ")}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2 px-5 pb-5 pt-4" aria-busy="true">
      <span className="sr-only">Loading calls…</span>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="h-10 animate-pulse rounded-lg bg-slate-100" />
      ))}
    </div>
  );
}

/** "3 calls · 1 went wrong", or nothing for a single call that went fine. */
function callsSummary(j: CallJob): string {
  const parts = [`${j.calls} ${j.calls === 1 ? "call" : "calls"}`];
  if (j.problems > 0 && j.calls > 1) parts.push(`${j.problems} went wrong`);
  return parts.join(" · ");
}

type Props = {
  range: CostRange;
  /** The page's filters plus this list's own status and search. */
  filters: CostFilters;
  onStatus: (status: StatusFilter | undefined) => void;
  onSearch: (search: string | undefined) => void;
  /** Narrow the whole tab to one learner (their spend, courses and jobs). */
  onLearner: (learner: string) => void;
  /** Search the last 90 days instead; offered when a search finds nothing. */
  onWiden: (() => void) | null;
};

/**
 * The AI calls in the period, grouped into jobs (one generation each, as Langfuse groups them
 * into traces), newest first, ten jobs at a time. Its own status filter and search narrow only
 * this list, and pick the jobs with at least one matching call. A table on wider screens and
 * stacked cards on a phone. Remount it (via `key`) when the period or filters change.
 */
const CostCallsTable = forwardRef<HTMLElement, Props>(function CostCallsTable({ range, filters, onStatus, onSearch, onLearner, onWiden }, ref) {
  const [jobs, setJobs] = useState<CallJob[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const request = useRef(0);
  const isPhone = useIsPhone();
  const rangeQuery = new URLSearchParams(costRangeParams(range)).toString();

  const load = useCallback(
    async (after: string | null) => {
      const id = ++request.current;
      try {
        const params = costFilterParams(filters);
        for (const [k, v] of costRangeParams(range)) params.set(k, v);
        if (after !== null) params.set("after", after);
        const body = await getJson<JobsResponse>(`/api/admin/ai-models/costs/jobs?${params}`, "calls");
        if (id !== request.current) return;
        setJobs((prev) => (after === null ? body.jobs : [...prev, ...body.jobs]));
        setNext(body.next);
        if (after === null) setTotal(body.total);
      } catch (e) {
        if (id === request.current) setError(e instanceof Error ? e.message : "Failed to load calls.");
      } finally {
        if (id === request.current) setLoading(false);
      }
    },
    [range, filters],
  );

  useEffect(() => {
    void load(null);
  }, [load]);

  const loadMore = () => {
    setLoading(true);
    setError(null);
    void load(jobs.length > 0 ? next : null);
  };

  const toggle = (key: string) =>
    setOpen((prev) => {
      const nextOpen = new Set(prev);
      if (nextOpen.has(key)) nextOpen.delete(key);
      else nextOpen.add(key);
      return nextOpen;
    });

  return (
    <section ref={ref} className="scroll-mt-36 rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
      <div className="px-5 pt-4">
        <h2 className="text-sm font-semibold text-slate-900">Calls</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          {jobs.length > 0 && total !== null
            ? `Showing ${jobs.length.toLocaleString()} of ${total.toLocaleString()} jobs, newest first. A job is one generation, with every AI call it made. Times are in Pakistan time.`
            : "Grouped by job: one generation, with every AI call it made. Times are in Pakistan time."}
        </p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-[13rem_minmax(0,1fr)]">
          <FilterListbox
            label="Status"
            value={filters.status ?? ""}
            onChange={(v) => onStatus((v || undefined) as StatusFilter | undefined)}
            options={STATUS_FILTERS.map((st) => ({
              key: st,
              label: STATUS_FILTER_LABELS[st],
            }))}
          />
          <SearchBox value={filters.search ?? ""} onSubmit={onSearch} />
        </div>
      </div>

      {jobs.length === 0 && loading ? <SkeletonRows /> : null}
      {jobs.length === 0 && !loading && !error ? (
        <div className="px-5 pb-5 pt-4 text-sm text-slate-500">
          <p>{filters.search ? `No calls found for "${filters.search}" in this period.` : "No calls match."}</p>
          {filters.search && onWiden ? (
            <button
              type="button"
              onClick={onWiden}
              className={`mt-2 rounded text-sm font-semibold text-indigo-600 hover:text-indigo-800 ${focusRing}`}
            >
              Look in the last 90 days
            </button>
          ) : null}
        </div>
      ) : null}

      {jobs.length > 0 ? (
        <>
          {/* Phone: one card per job. */}
          {isPhone ? (
            <ul className="mt-3 divide-y divide-slate-100 border-t border-slate-100">
              {jobs.map((j) => {
                const isOpen = open.has(j.key);
                return (
                  <li key={j.key}>
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={`job-${j.key}`}
                      onClick={() => toggle(j.key)}
                      className={`block w-full px-4 py-3 text-left ${isOpen ? "bg-slate-50/70" : ""} ${focusRing}`}
                    >
                      <span className="flex items-start justify-between gap-3">
                        <span className="min-w-0 text-sm font-medium text-slate-900">{taskWithLanguage(j)}</span>
                        <StatusBadge status={j.status} />
                      </span>
                      <span className="mt-1 flex items-baseline justify-between gap-3 text-xs text-slate-500">
                        <span className="min-w-0">
                          {[j.organization, j.course, personText(j), formatPakistanTime(j.endedAt), j.calls > 1 ? callsSummary(j) : null]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                        <span className="shrink-0 text-sm font-semibold tabular-nums text-slate-900">{costUsd(j.cost)}</span>
                      </span>
                    </button>
                    {isOpen ? (
                      <div id={`job-${j.key}`}>
                        <JobDetails job={j} rangeQuery={rangeQuery} onLearner={onLearner} />
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            /* Wider screens: a table. */
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[64rem] table-fixed text-left text-sm">
                <colgroup>
                  <col className="w-10" />
                  <col className="w-[10rem]" />
                  <col className="w-[6.5rem]" />
                  <col className="w-[6rem]" />
                  <col />
                  <col />
                  <col className="w-[11%]" />
                  <col className="w-[11%]" />
                  <col className="w-[13%]" />
                  <col className="w-[7.5rem]" />
                </colgroup>
                <thead>
                  <tr className="whitespace-nowrap border-y border-slate-100 bg-slate-50/60 text-xs font-semibold text-slate-500">
                    <th className="py-2 pl-5" aria-label="Details" />
                    <th className="px-3 py-2 font-semibold">Time</th>
                    <th className="px-3 py-2 font-semibold">Status</th>
                    <th className="px-3 py-2 text-right font-semibold">Cost</th>
                    <th className="px-3 py-2 font-semibold">Task</th>
                    <th className="px-3 py-2 font-semibold">Lesson</th>
                    <th className="px-3 py-2 font-semibold">Course</th>
                    <th className="px-3 py-2 font-semibold">Organization</th>
                    <th className="px-3 py-2 font-semibold">Triggered by</th>
                    <th className="px-3 py-2 pr-5 font-semibold">Calls</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((j) => {
                    const isOpen = open.has(j.key);
                    const Chevron = isOpen ? ChevronDown : ChevronRight;
                    const lesson = lessonText(j);
                    return (
                      <Fragment key={j.key}>
                        <tr
                          onClick={() => toggle(j.key)}
                          className={`cursor-pointer border-t border-slate-100 align-top hover:bg-slate-50 ${isOpen ? "bg-slate-50/70" : ""}`}
                        >
                          <td className="py-2.5 pl-5">
                            <button
                              type="button"
                              aria-expanded={isOpen}
                              aria-controls={`job-row-${j.key}`}
                              aria-label={isOpen ? "Hide details" : "Show details"}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggle(j.key);
                              }}
                              className={`rounded p-0.5 text-slate-400 hover:text-slate-700 ${focusRing}`}
                            >
                              <Chevron className="h-4 w-4" />
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-slate-500">{formatPakistanTime(j.endedAt)}</td>
                          <td className="px-3 py-2.5">
                            <StatusBadge status={j.status} />
                          </td>
                          <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold tabular-nums text-slate-900">
                            {costUsd(j.cost)}
                          </td>
                          <td className="truncate px-3 py-2.5 text-slate-900" title={taskWithLanguage(j)}>
                            {taskWithLanguage(j)}
                          </td>
                          <td className="truncate px-3 py-2.5 text-slate-700" title={lesson ?? undefined}>
                            {lesson ?? <span className="text-slate-400">-</span>}
                          </td>
                          <td className="truncate px-3 py-2.5 text-slate-700" title={j.course ?? undefined}>
                            {j.course ?? <span className="text-slate-400">-</span>}
                          </td>
                          <td className="truncate px-3 py-2.5 text-slate-700" title={j.organization ?? undefined}>
                            {j.organization ? (
                              <span className="inline-flex max-w-full truncate rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-700">
                                {j.organization}
                              </span>
                            ) : (
                              <span className="text-slate-400">-</span>
                            )}
                          </td>
                          <td className={`truncate px-3 py-2.5 ${j.learner ? "text-slate-700" : "text-slate-500"}`} title={personText(j)}>
                            {personText(j)}
                          </td>
                          <td
                            className={`truncate px-3 py-2.5 pr-5 text-xs ${j.problems > 0 && j.calls > 1 ? "text-amber-700" : "text-slate-500"}`}
                          >
                            {callsSummary(j)}
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr id={`job-row-${j.key}`}>
                            <td colSpan={10} className="p-0">
                              <JobDetails job={j} rangeQuery={rangeQuery} onLearner={onLearner} />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}

      {error ? (
        <div className="mx-5 my-4 flex items-center gap-2 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
        </div>
      ) : null}

      {(loading && jobs.length > 0) || next !== null || error ? (
        <div className="flex justify-center border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            disabled={loading}
            onClick={loadMore}
            className={`inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-60 ${focusRing}`}
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ChevronDown className="h-3.5 w-3.5" />}
            {loading ? "Loading…" : error ? "Try again" : "Show 10 more"}
          </button>
        </div>
      ) : null}
    </section>
  );
});

export default CostCallsTable;
